import React from "react";

/**
 * AuroraBackground — premium animated mesh gradient background.
 *
 * Pure CSS, no dependency. Designed for stark dark backgrounds where four
 * floating blurred orbs drift very slowly across the canvas, with a fine grain
 * overlay to break up the gradient banding.
 *
 * Usage:
 *   <AuroraBackground className="absolute inset-0" />
 */
export default function AuroraBackground({ className = "absolute inset-0", intensity = 1 }) {
  const opacity = Math.min(1, Math.max(0.2, intensity));
  return (
    <div className={`${className} overflow-hidden pointer-events-none`} aria-hidden="true">
      {/* Base */}
      <div className="absolute inset-0 bg-[var(--bg)]" />

      {/* 4 floating orbs */}
      <div
        className="absolute aurora-orb"
        style={{
          width: "60vmax",
          height: "60vmax",
          top: "-20%",
          left: "-10%",
          background: "radial-gradient(closest-side, rgba(0,220,130,0.55), rgba(0,220,130,0) 70%)",
          filter: "blur(60px)",
          opacity: 0.7 * opacity,
          animation: "auroraDrift1 28s ease-in-out infinite",
        }}
      />
      <div
        className="absolute aurora-orb"
        style={{
          width: "55vmax",
          height: "55vmax",
          top: "30%",
          right: "-15%",
          background: "radial-gradient(closest-side, rgba(217,70,239,0.42), rgba(217,70,239,0) 70%)",
          filter: "blur(70px)",
          opacity: 0.55 * opacity,
          animation: "auroraDrift2 34s ease-in-out infinite",
        }}
      />
      <div
        className="absolute aurora-orb"
        style={{
          width: "50vmax",
          height: "50vmax",
          bottom: "-25%",
          left: "20%",
          background: "radial-gradient(closest-side, rgba(56,189,248,0.32), rgba(56,189,248,0) 70%)",
          filter: "blur(80px)",
          opacity: 0.5 * opacity,
          animation: "auroraDrift3 40s ease-in-out infinite",
        }}
      />
      <div
        className="absolute aurora-orb"
        style={{
          width: "40vmax",
          height: "40vmax",
          top: "5%",
          right: "20%",
          background: "radial-gradient(closest-side, rgba(244,114,182,0.22), rgba(244,114,182,0) 70%)",
          filter: "blur(90px)",
          opacity: 0.45 * opacity,
          animation: "auroraDrift4 46s ease-in-out infinite reverse",
        }}
      />

      {/* Grain texture — breaks up gradient banding */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/><feColorMatrix values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.16 0'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.45'/></svg>\")",
          mixBlendMode: "overlay",
          opacity: 0.7,
        }}
      />

      {/* Subtle vignette */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.45) 100%)",
        }}
      />

      <style>{`
        @keyframes auroraDrift1 { 0%,100% { transform: translate(0,0) rotate(0); } 50% { transform: translate(8vw, 6vh) rotate(8deg); } }
        @keyframes auroraDrift2 { 0%,100% { transform: translate(0,0) rotate(0); } 50% { transform: translate(-10vw, 8vh) rotate(-6deg); } }
        @keyframes auroraDrift3 { 0%,100% { transform: translate(0,0); } 50% { transform: translate(6vw, -8vh); } }
        @keyframes auroraDrift4 { 0%,100% { transform: translate(0,0); } 50% { transform: translate(-6vw, 5vh); } }
      `}</style>
    </div>
  );
}
